"use client";

import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getRelatedJobs, selectJobState } from "./RelatedJobsSlice";
import {
  MapPinIcon,
  BriefcaseIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  TagIcon,
  ArrowLongRightIcon,
  CurrencyRupeeIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { Image, Badge, Tooltip as ChakraTooltip, Avatar } from "@chakra-ui/react";
import { EMPLOYER_COMPANY_LOGO_SAVE_URL } from "../../../common/URLs";
// Import Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";
// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
// Import required Swiper modules
import { Pagination, Autoplay } from "swiper/modules";
import { TimeDifference } from "../../../common/TimeDifference";

const RelatedJobs = ({ uuid }) => {
  const dispatch = useDispatch();
  const relatedJobs = useSelector(selectJobState);
  const swiperRef = useRef(null);

  useEffect(() => {
    if (uuid) {
      dispatch(getRelatedJobs(uuid));
    }
  }, [uuid, dispatch]);

  // Handle mouse enter to pause autoplay
  const handleMouseEnter = () => {
    if (swiperRef.current && swiperRef.current.autoplay) {
      swiperRef.current.autoplay.stop();
    }
  };

  // Handle mouse leave to resume autoplay
  const handleMouseLeave = () => {
    if (swiperRef.current && swiperRef.current.autoplay) {
      swiperRef.current.autoplay.start();
    }
  };

  if (!relatedJobs || relatedJobs.length === 0) {
    return (
      <p className="text-center text-gray-500 py-8">No related jobs found.</p>
    );
  }

  return (
    <div className="bg-gray-50 py-8 px-4 md:px-6 lg:px-8 rounded-xl">
      <div className="flex flex-wrap justify-between items-center mb-6">
        <div className="px-2">
          <h3 className="text-2xl font-bold text-gray-800">
            Related Jobs For You
          </h3>
          <h6 className="text-gray-600 font-normal mt-1">
            Know your worth and find the job that qualifies your life
          </h6>
        </div>
        <Link to={`/jobs`} className="group">
          <button className="text-blue-600 font-medium flex items-center hover:text-blue-700 transition-colors">
            Browse all jobs
            <ArrowLongRightIcon className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
          </button>
        </Link>
      </div>

      <Swiper
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        slidesPerView={1}
        spaceBetween={20}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        modules={[Pagination, Autoplay]}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        breakpoints={{
          640: {
            slidesPerView: 1,
            spaceBetween: 20,
          },
          768: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 20,
          },
        }}
        className="related-jobs-swiper"
      >
        {relatedJobs.map((job) => (
          <SwiperSlide key={job.uuid}>
            <div
              className="job-card border border-gray-200 rounded-xl p-3 bg-white shadow-sm hover:shadow-md transition-shadow h-full flex flex-col"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <div className="flex items-center mb-4 gap-3">
                <div className="relative">
                  {job?.company_logo ? (
                    <Image
                      src={`${EMPLOYER_COMPANY_LOGO_SAVE_URL}${job.company_logo}`}
                      fallbackSrc="https://via.placeholder.com/64"
                      alt={job.hiring_company_name || "Company logo"}
                      className="h-16 w-16 object-cover rounded-lg shadow-sm"
                    />
                  ) : (
                    <Avatar
                      name={job.company_name}
                    />
                  )}
                </div>
                <div>
                  <ChakraTooltip
                    label={"Job Position"}
                    placement="top"
                    fontSize="sm"
                    borderRadius="md"
                    hasArrow
                  >
                    <div className="job-title text-lg font-semibold text-gray-800 line-clamp-1 transition-colors">
                      {job.job_name || "Job Name"}
                    </div>
                  </ChakraTooltip>
                  <ChakraTooltip
                    label={"Company Name"}
                    placement="top"
                    fontSize="sm"
                    borderRadius="md"
                    hasArrow
                  >
                    <p className="company-name text-gray-500 text-sm transition-colors">
                      {job.hiring_company_name}
                    </p>
                  </ChakraTooltip>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 mb-4">
                <div className="flex items-center tooltip-container">
                  <BuildingOfficeIcon className="w-5 h-5 mr-2  text-gray-400" />
                  <ChakraTooltip
                    label={"Designation"}
                    placement="top"
                    fontSize="sm"
                    borderRadius="md"
                    hasArrow
                  >
                    <span className="text-sm text-gray-700">
                      {job.job_title?.title || "Not specified"}
                    </span>
                  </ChakraTooltip>
                </div>
                <div className="flex items-center">
                  <BriefcaseIcon className="w-5 h-5 mr-2  text-gray-400" />
                  <ChakraTooltip
                    label={`Employment Type`}
                    placement="top"
                    fontSize="sm"
                    borderRadius="md"
                    hasArrow
                  >
                    <span className="text-sm text-gray-700">
                      <Badge
                        colorScheme="blue"
                        variant="subtle"
                        px={2}
                        py={0.5}
                        borderRadius="md"
                      >
                        {job.job_type || "Full-time"}
                      </Badge>
                    </span>
                  </ChakraTooltip>
                </div>
                {job.job_tag && (
                  <div className="flex items-center">
                    <TagIcon className="w-5 h-5 mr-2  text-gray-400" />
                    <ChakraTooltip
                      label={`Category`}
                      placement="top"
                      fontSize="sm"
                      borderRadius="md"
                      hasArrow
                    >
                      <span className="text-sm capitalize text-gray-700">
                        {job.job_tag}
                      </span>
                    </ChakraTooltip>
                  </div>
                )}
                <div className="flex items-center">
                  <CurrencyRupeeIcon className="w-5 h-5 mr-2  text-gray-400" />
                  <ChakraTooltip
                    label={`Salary Range: ${job.salary_min_amount}-${job.salary_max_amount
                      } ${job.salary_per_type || "Yearly"}`}
                    placement="top"
                    fontSize="sm"
                    borderRadius="md"
                    hasArrow
                  >
                    <span className="text-sm text-gray-700">
                      {job.salary_min_amount === null ||
                        job.salary_min_amount === 0
                        ? "Not Disclosed"
                        : `₹${job.salary_min_amount || "N/A"} - ₹${job.salary_max_amount || "N/A"
                        } / ${job.salary_per_type || "Not Specified"}`}
                    </span>
                  </ChakraTooltip>
                </div>
                <div className="flex items-start">
                  <MapPinIcon className="w-5 h-5 mr-2  text-gray-400 mt-0.5" />
                  <ChakraTooltip
                    label={"Job Location"}
                    placement="top"
                    fontSize="sm"
                    borderRadius="md"
                    hasArrow
                  >
                    <span className="text-sm text-gray-700 line-clamp-1">
                      {job.job_locations?.length > 0
                        ? job.job_locations
                          .map((loc) => loc.job_location.title)
                          .join(", ")
                        : `${job.city?.name || ""}, ${job.state?.name || ""}, ${job.country?.name || ""
                        }`}
                    </span>
                  </ChakraTooltip>
                </div>
                <div className="flex items-center">
                  <CalendarIcon className="w-5 h-5 mr-2  text-gray-400" />
                  <TimeDifference dateTimeString={job.job_post_date} />
                </div>
              </div>

              <div className="mt-auto">
                <Link key={job.uuid} to={`/jobs/${job.uuid}`}>
                  <button className="w-full bg-blue-600 text-white py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center">
                    View Job Details
                  </button>
                </Link>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Add custom styles for the Swiper pagination and hover effects */}
      <style jsx>{`
        .related-jobs-swiper {
          padding-bottom: 50px;
        }
        :global(.swiper-pagination-bullet-active) {
          background-color: #3b82f6;
        }

        /* Hover effects for job title and company name */
        .job-card:hover .job-title {
          color: #2563eb; /* blue-600 */
        }

        /* Custom tooltip styles */
        :global(.chakra-tooltip) {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
          padding: 6px 10px;
          font-size: 12px;
          z-index: 9999;
        }
      `}</style>
    </div>
  );
};

export default RelatedJobs;
